import { useState, useEffect } from "react";
import { ChevronUp, Shield, Mail, ExternalLink } from "lucide-react";

const WhosRankingPrivacy = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="whosranking-privacy-page">
      <main className="pp-main" data-testid="whosranking-privacy-content">
        <article className="pp-article">
          <header className="pp-header" data-testid="whosranking-privacy-header">
            <div className="pp-header-icon">
              <Shield size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Privacy Policy — Who's Ranking on G</h1>
            <p className="pp-last-updated">Last updated: October 4, 2026</p>
          </header>

          <section className="pp-section" data-testid="section-wr-intro">
            <div className="pp-highlight-box">
              <p>
                Radhesh Gowd ("we", "us") makes the Who's Ranking on G iPhone app. The app shows
                which businesses are paying Google to appear for a search in your city, and
                lets you book a call with us. This policy explains what we collect, why, who
                else handles it, and how to have it deleted.
              </p>
            </div>
          </section>

          <section className="pp-section" data-testid="section-wr-collect">
            <h2 className="pp-section-heading">What we collect</h2>
            <ul className="pp-list">
              <li>
                <strong>When you search.</strong> The service and city you enter. We use them
                to look up who ranks and advertises for that search. Results are saved for up
                to 7 days so repeat searches load quickly. A search isn't linked to you unless
                you go on to send us your details.
              </li>
              <li>
                <strong>When you send the form.</strong> Your name and phone number, plus
                anything else you choose to give: business name, business website, email
                address, whether you run Google Ads, and your monthly ad spend. We also record
                the search you ran, the summary you were shown, and when you sent the form.
              </li>
              <li>
                <strong>When you book a call.</strong> Booking happens on Cal.com, shown inside
                the app. We pass your name and email to Cal.com so you don't have to type them
                again. What you enter there, and your booking, are handled by Cal.com under its
                own privacy policy.
              </li>
              <li>
                <strong>When you view your results.</strong> Our video is streamed by
                Cloudflare and starts loading when your results appear, so it plays without a
                wait.
              </li>
              <li>
                <strong>Your IP address.</strong> Our server uses it to limit how many searches
                and form submissions can be made in an hour. It is held in memory for up to an
                hour and isn't saved.
              </li>
            </ul>
            <p>
              <strong>What we don't do.</strong> There are no accounts, no ads, no analytics
              tools, and no tracking of you across other apps or websites. We don't sell your
              personal information.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wr-use">
            <h2 className="pp-section-heading">How we use it</h2>
            <ul className="pp-list">
              <li>To show you the results of your search.</li>
              <li>
                To contact you about your request, by phone call, text message, or email.
              </li>
              <li>To prepare for and hold your call.</li>
            </ul>
            <p>
              We won't use your details for anything else without asking you first.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wr-providers">
            <h2 className="pp-section-heading">Who else handles it</h2>
            <p>We use these service providers, only for the purposes above:</p>
            <ul className="pp-list">
              <li>
                <strong>Cal.com</strong> runs the booking calendar. It receives your name and
                email, and anything you enter when booking.{" "}
                <a
                  href="https://cal.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pp-link"
                  data-testid="wr-calcom-link"
                >
                  Cal.com privacy policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>Resend</strong> sends us an email when you submit the form. That email
                contains your form details.{" "}
                <a
                  href="https://resend.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pp-link"
                  data-testid="wr-resend-link"
                >
                  Resend privacy policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>Cloudflare</strong> streams the video. It receives technical
                information such as your IP address.{" "}
                <a
                  href="https://www.cloudflare.com/privacypolicy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pp-link"
                  data-testid="wr-cloudflare-link"
                >
                  Cloudflare privacy policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>SerpApi</strong> and <strong>Bright Data</strong> run the Google
                searches behind your results. They receive the service and city you searched,
                not your personal details.{" "}
                <a
                  href="https://serpapi.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pp-link"
                  data-testid="wr-serpapi-link"
                >
                  SerpApi privacy policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>{" "}
                ·{" "}
                <a
                  href="https://brightdata.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pp-link"
                  data-testid="wr-brightdata-link"
                >
                  Bright Data privacy policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                The company that hosts our server stores the database where your form details
                are kept.
              </li>
            </ul>
            <p>
              Each of these providers protects your information at least as well as this policy
              describes.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wr-retention">
            <h2 className="pp-section-heading">How long we keep it</h2>
            <p>
              We keep your form details for as long as we need them to follow up with you and
              keep business records, and we delete them sooner if you ask. Saved search results
              expire after 7 days.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wr-choices">
            <h2 className="pp-section-heading">Your choices</h2>
            <ul className="pp-list">
              <li>
                <strong>Stop hearing from us, or delete your details:</strong> email{" "}
                <a
                  href="mailto:support@radships.com"
                  className="pp-link"
                  data-testid="wr-choices-email"
                >
                  support@radships.com
                </a>
                . We'll stop contacting you and delete your form details.
              </li>
              <li>
                <strong>Cancel or change a booking:</strong> use the link in your Cal.com
                confirmation email.
              </li>
              <li>
                <strong>Details you gave Cal.com:</strong> contact Cal.com, or ask us and we'll
                help.
              </li>
            </ul>
          </section>

          <section className="pp-section" data-testid="section-wr-children">
            <h2 className="pp-section-heading">Children</h2>
            <p>
              This app is for business owners. It isn't meant for children under 13, and we
              don't knowingly collect their information.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wr-changes">
            <h2 className="pp-section-heading">Changes</h2>
            <p>
              If we change how we handle your information, we'll update this page and the date
              at the top.
            </p>
          </section>

          <section className="pp-section" data-testid="section-wr-contact">
            <h2 className="pp-section-heading">Contact</h2>
            <div className="pp-contact-card" data-testid="wr-contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="wr-contact-email"
              >
                <Mail size={16} />
                support@radships.com
              </a>
            </div>
          </section>

          <footer className="pp-footer" data-testid="whosranking-privacy-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="wr-privacy-back-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default WhosRankingPrivacy;
